import { BOARD_SIZE } from '../constants';
import type { IBattleshipGameState, ICell, TShotsGrid } from '../types';
import { getShipAt } from '../utils/check-cell-hit';
import { isShipSunk } from '../utils/check-all-sunk';

function getUnshotCells(shotsGrid: TShotsGrid): ICell[] {
  const cells: ICell[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (shotsGrid[r][c] === null) cells.push({ row: r, col: c });
    }
  }
  return cells;
}

const ORTHO: readonly [number, number][] = [
  [-1, 0],
  [1, 0],
  [0, -1],
  [0, 1],
];

function cellKey(row: number, col: number): string {
  return `${row},${col}`;
}

function getActiveHitCells(
  shots: TShotsGrid,
  targetShips: IBattleshipGameState['ships0'],
): ICell[] {
  const out: ICell[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (let c = 0; c < BOARD_SIZE; c++) {
      if (shots[r][c] !== 'hit') continue;
      const idx = getShipAt(targetShips, r, c);
      if (idx < 0) continue;
      if (isShipSunk(targetShips[idx], shots)) continue;
      out.push({ row: r, col: c });
    }
  }
  return out;
}

function buildActiveHitSet(cells: ICell[]): Set<string> {
  return new Set(cells.map(({ col, row }) => cellKey(row, col)));
}

function lineEndpointsForRow(
  shots: TShotsGrid,
  row: number,
  activeSet: Set<string>,
): ICell[] {
  const cols: number[] = [];
  for (let c = 0; c < BOARD_SIZE; c++) {
    if (shots[row][c] === 'hit' && activeSet.has(cellKey(row, c))) {
      cols.push(c);
    }
  }
  if (cols.length === 0) return [];
  cols.sort((a, b) => a - b);
  const ends: ICell[] = [];
  let i = 0;
  while (i < cols.length) {
    let j = i;
    while (j + 1 < cols.length && cols[j + 1] === cols[j] + 1) j++;
    const lo = cols[i];
    const hi = cols[j];
    if (lo > 0 && shots[row][lo - 1] === null) {
      ends.push({ row, col: lo - 1 });
    }
    if (hi < BOARD_SIZE - 1 && shots[row][hi + 1] === null) {
      ends.push({ row, col: hi + 1 });
    }
    i = j + 1;
  }
  return ends;
}

function lineEndpointsForRowMinLen(
  shots: TShotsGrid,
  row: number,
  activeSet: Set<string>,
  minLen: number,
): ICell[] {
  const cols: number[] = [];
  for (let c = 0; c < BOARD_SIZE; c++) {
    if (shots[row][c] === 'hit' && activeSet.has(cellKey(row, c))) {
      cols.push(c);
    }
  }
  if (cols.length === 0) return [];
  cols.sort((a, b) => a - b);
  const ends: ICell[] = [];
  let i = 0;
  while (i < cols.length) {
    let j = i;
    while (j + 1 < cols.length && cols[j + 1] === cols[j] + 1) j++;
    const lo = cols[i];
    const hi = cols[j];
    const len = hi - lo + 1;
    if (len >= minLen) {
      if (lo > 0 && shots[row][lo - 1] === null) {
        ends.push({ row, col: lo - 1 });
      }
      if (hi < BOARD_SIZE - 1 && shots[row][hi + 1] === null) {
        ends.push({ row, col: hi + 1 });
      }
    }
    i = j + 1;
  }
  return ends;
}

function lineEndpointsForCol(
  shots: TShotsGrid,
  col: number,
  activeSet: Set<string>,
): ICell[] {
  const rows: number[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    if (shots[r][col] === 'hit' && activeSet.has(cellKey(r, col))) {
      rows.push(r);
    }
  }
  if (rows.length === 0) return [];
  rows.sort((a, b) => a - b);
  const ends: ICell[] = [];
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j + 1 < rows.length && rows[j + 1] === rows[j] + 1) j++;
    const lo = rows[i];
    const hi = rows[j];
    if (lo > 0 && shots[lo - 1][col] === null) {
      ends.push({ row: lo - 1, col });
    }
    if (hi < BOARD_SIZE - 1 && shots[hi + 1][col] === null) {
      ends.push({ row: hi + 1, col });
    }
    i = j + 1;
  }
  return ends;
}

function lineEndpointsForColMinLen(
  shots: TShotsGrid,
  col: number,
  activeSet: Set<string>,
  minLen: number,
): ICell[] {
  const rows: number[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    if (shots[r][col] === 'hit' && activeSet.has(cellKey(r, col))) {
      rows.push(r);
    }
  }
  if (rows.length === 0) return [];
  rows.sort((a, b) => a - b);
  const ends: ICell[] = [];
  let i = 0;
  while (i < rows.length) {
    let j = i;
    while (j + 1 < rows.length && rows[j + 1] === rows[j] + 1) j++;
    const lo = rows[i];
    const hi = rows[j];
    const len = hi - lo + 1;
    if (len >= minLen) {
      if (lo > 0 && shots[lo - 1][col] === null) {
        ends.push({ row: lo - 1, col });
      }
      if (hi < BOARD_SIZE - 1 && shots[hi + 1][col] === null) {
        ends.push({ row: hi + 1, col });
      }
    }
    i = j + 1;
  }
  return ends;
}

function collectLineEndpoints(
  shots: TShotsGrid,
  activeSet: Set<string>,
): ICell[] {
  const seen = new Set<string>();
  const out: ICell[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (const cell of lineEndpointsForRow(shots, r, activeSet)) {
      const k = cellKey(cell.row, cell.col);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(cell);
    }
  }
  for (let c = 0; c < BOARD_SIZE; c++) {
    for (const cell of lineEndpointsForCol(shots, c, activeSet)) {
      const k = cellKey(cell.row, cell.col);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(cell);
    }
  }
  return out;
}

function collectLineEndpointsMinLen(
  shots: TShotsGrid,
  activeSet: Set<string>,
  minLen: number,
): ICell[] {
  const seen = new Set<string>();
  const out: ICell[] = [];
  for (let r = 0; r < BOARD_SIZE; r++) {
    for (const cell of lineEndpointsForRowMinLen(shots, r, activeSet, minLen)) {
      const k = cellKey(cell.row, cell.col);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(cell);
    }
  }
  for (let c = 0; c < BOARD_SIZE; c++) {
    for (const cell of lineEndpointsForColMinLen(shots, c, activeSet, minLen)) {
      const k = cellKey(cell.row, cell.col);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(cell);
    }
  }
  return out;
}

function orthAdjacentUnshot(shots: TShotsGrid, activeHits: ICell[]): ICell[] {
  const seen = new Set<string>();
  const out: ICell[] = [];
  for (const { col, row } of activeHits) {
    for (const [dr, dc] of ORTHO) {
      const r = row + dr;
      const c = col + dc;
      if (r < 0 || r >= BOARD_SIZE || c < 0 || c >= BOARD_SIZE) continue;
      if (shots[r][c] !== null) continue;
      const k = cellKey(r, c);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push({ row: r, col: c });
    }
  }
  return out;
}

function pickRandom<T>(items: T[], random: () => number): T {
  return items[Math.floor(random() * items.length)];
}

function pickWeightedUnshot(
  unshot: ICell[],
  d: number,
  random: () => number,
): ICell {
  if (unshot.length === 0) return { row: 0, col: 0 };
  if (unshot.length === 1) return unshot[0];

  const weights = unshot.map(({ col: c, row: r }) => {
    let w = 1;
    if (d > 0) {
      const checker = (r + c) % 2 === 0 ? 1.5 * d * d : 0;
      const dist = Math.abs(r - 4.5) + Math.abs(c - 4.5);
      const center = Math.max(0, (1 - dist / 18) * d * 0.85);
      w += checker + center;
    }
    return w;
  });

  let total = 0;
  for (const w of weights) total += w;
  let t = random() * total;
  for (let i = 0; i < unshot.length; i++) {
    t -= weights[i];
    if (t <= 0) return unshot[i];
  }
  return unshot[unshot.length - 1];
}

export type TBattleshipBotRandom = () => number;

export class BattleshipBot {
  private readonly difficulty: number;
  private readonly random: TBattleshipBotRandom;

  constructor(
    difficulty01: number,
    random: TBattleshipBotRandom = Math.random,
  ) {
    this.difficulty = Math.max(0, Math.min(1, difficulty01));
    this.random = random;
  }

  getMove(
    state: IBattleshipGameState,
    playerSlot: 0 | 1,
  ): { row: number; col: number } {
    const shotsAtTarget = playerSlot === 0 ? state.shotsAt1 : state.shotsAt0;
    const targetShips = playerSlot === 0 ? state.ships1 : state.ships0;

    const unshot = getUnshotCells(shotsAtTarget);
    if (unshot.length === 0) {
      return { row: 0, col: 0 };
    }

    const activeHits = getActiveHitCells(shotsAtTarget, targetShips);

    if (activeHits.length === 0) {
      return pickWeightedUnshot(unshot, this.difficulty, this.random);
    }

    const activeSet = buildActiveHitSet(activeHits);
    const lineEndsLen2 = collectLineEndpointsMinLen(
      shotsAtTarget,
      activeSet,
      2,
    );
    if (this.difficulty > 0 && lineEndsLen2.length > 0) {
      return pickRandom(lineEndsLen2, this.random);
    }

    const lineEnds = collectLineEndpoints(shotsAtTarget, activeSet);
    const adjacent = orthAdjacentUnshot(shotsAtTarget, activeHits);

    const d = this.difficulty;
    const strayProb = (1 - d) * (1 - d);
    if (this.random() < strayProb) {
      if (adjacent.length > 0) return pickRandom(adjacent, this.random);
      if (lineEnds.length > 0) return pickRandom(lineEnds, this.random);
      return pickWeightedUnshot(unshot, d, this.random);
    }

    if (lineEnds.length > 0 && this.random() < d * d) {
      return pickRandom(lineEnds, this.random);
    }

    if (adjacent.length > 0 && this.random() < d) {
      return pickRandom(adjacent, this.random);
    }

    if (adjacent.length > 0) {
      return pickRandom(adjacent, this.random);
    }

    if (lineEnds.length > 0) {
      return pickRandom(lineEnds, this.random);
    }

    return pickWeightedUnshot(unshot, d, this.random);
  }
}
